import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Space,
	Radio,
	Divider,
	Input,
	Table,
	Select,
	Typography,
	Pagination,
} from 'antd';
import { PlusOutlined, DeleteOutlined, LockOutlined, GlobalOutlined, UsergroupAddOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import AddUniversityPermissionModal from './AddUniversityPermissionModal';
import { permissionOptions } from '../../utils/sharePermission';
import FormItemDropdown from '../form-dropdown/FormItemDropdown';
import { getAllMultiLevelUniversities } from '../../service/learning-material';

const { Search } = Input;
const { Option } = Select;

// Constants cho access modes
const ACCESS_MODES = {
	RESTRICTED: '1',    // Hạn chế
	PUBLIC: '2',        // Công khai
	UNIVERSITY: '3',    // Đơn vị đào tạo
} as const;

// Constants cho permissions
const PERMISSION_LEVELS = {
	VIEW: '2',          // Xem
	EDIT: '3',          // Chỉnh sửa
};

type ShareMode = typeof ACCESS_MODES[keyof typeof ACCESS_MODES];

export interface UniversityNode {
	id: number;
	uuid?: string;
	name: string;
	children?: UniversityNode[] | null;
	isDaiHoc?: boolean;
	universityId?: number;
	nameEn?: string;
	code?: string;
	logo?: string;
	canManage?: boolean;
}

export interface ShareUniNode {
	uuid: string;
	name: string;
	permission: string;
	parentUuid: any;
	children?: ShareUniNode[];
	code?: string;
	isDaiHoc?: boolean;
}

const flatToTree = (arr: ShareUniNode[]): ShareUniNode[] => {
	const map: Record<string, ShareUniNode> = {};
	arr.forEach((x) => (map[x.uuid] = { ...x, children: [] }));
	const roots: ShareUniNode[] = [];
	arr.forEach((x) => {
		if (x.parentUuid !== null && map[x.parentUuid]) {
			map[x.parentUuid].children!.push(map[x.uuid]);
		} else {
			roots.push(map[x.uuid]);
		}
	});
	return roots;
};

interface Props {
	universitiesList?: any[];
	initialMode?: string;
	initialPublicPermission?: string;
	onDataChange?: (data: {
		mode: string;
		universities: any[];
		publicPermission: string;
	}) => void;
}

const SharePermissionPublicTab = (props: Props) => {
  const { t } = useTranslation();
	const {
		universitiesList,
		initialMode,
		initialPublicPermission,
		onDataChange
	} = props;

	const [mode, setMode] = useState<ShareMode>(ACCESS_MODES.RESTRICTED);
	const [publicPermission, setPublicPermission] = useState<string>(PERMISSION_LEVELS.VIEW);
	const [data, setData] = useState<ShareUniNode[]>([]);
	const [keyword, setKeyword] = useState('');
	const [open, setOpen] = useState(false);
	const [allUniversities, setAllUniversities] = useState<UniversityNode[]>([]);
	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const findNodeInTree = (nodes: UniversityNode[], uuid: string): UniversityNode | null => {
		for (const node of nodes) {
			if (node.uuid === uuid) {
				return node;
			}
			if (node.children && node.children.length > 0) {
				const found = findNodeInTree(node.children, uuid);
				if (found) return found;
			}
		}
		return null;
	};

	const findParentInTree = (nodes: UniversityNode[], uuid: string, parent: UniversityNode | null = null): UniversityNode | null => {
		for (const node of nodes) {
			if (node.uuid === uuid) {
				return parent;
			}
			if (node.children && node.children.length > 0) {
				const found = findParentInTree(node.children, uuid, node);
				if (found) return found;
			}
		}
		return null;
	};

	useEffect(() => {
		(async () => {
			try {
				const res = await getAllMultiLevelUniversities({});
				const list: UniversityNode[] = res?.data?.data?.content ?? [];
				setAllUniversities(list);
			} catch (error) {
				console.error('Error loading universities:', error);
			}
		})();
	}, []);

	const collectParentNodes = (
		nodeUuid: string,
		results: Map<string, ShareUniNode>
	) => {
		const found = findNodeInTree(allUniversities, nodeUuid);
		if (found) {
			const parent = findParentInTree(allUniversities, nodeUuid);
			if (parent && parent.uuid && !results.has(parent.uuid)) {
				results.set(parent.uuid, {
					uuid: parent.uuid,
					name: parent.name,
					permission: PERMISSION_LEVELS.VIEW,
					parentUuid: null,
					code: parent.code,
					isDaiHoc: parent.isDaiHoc || true
				});
				collectParentNodes(parent.uuid, results);
			}
		}
	};

	// Set initial values based on props
	useEffect(() => {
		if (initialMode) {
			setMode(initialMode as ShareMode);
		}
		if (initialPublicPermission) {
			setPublicPermission(initialPublicPermission);
		}
	}, [initialMode, initialPublicPermission]);

	// Load universities when mode is UNIVERSITY
	useEffect(() => {
		if (mode === ACCESS_MODES.UNIVERSITY && universitiesList && universitiesList.length > 0 && allUniversities.length > 0) {
			const results = new Map<string, ShareUniNode>();

			universitiesList.forEach(uni => {
				const universityUuid = uni.enterpriseId;
				const foundNode = findNodeInTree(allUniversities, universityUuid);

				if (foundNode && foundNode.uuid) {
					const parent = findParentInTree(allUniversities, universityUuid);
					results.set(universityUuid, {
						uuid: universityUuid,
						name: foundNode.name,
						permission: uni.permission.toString(),
						parentUuid: parent?.uuid ?? null,
						code: foundNode.code,
						isDaiHoc: foundNode.isDaiHoc
					});

					collectParentNodes(universityUuid, results);
				}
			});

			const mappedUniversities = Array.from(results.values());
			setData(mappedUniversities);
		}
	}, [mode, universitiesList, allUniversities]);

	// Notify parent component when data changes
	useEffect(() => {
		if (onDataChange) {
			const transformedData = data
				.filter(item => !item.isDaiHoc)
				.map(item => ({
					enterpriseId: item.uuid,
					permission: parseInt(item.permission)
				}));

			onDataChange({
				mode,
				universities: transformedData,
				publicPermission: publicPermission
			});
		}
	}, [mode, data, publicPermission, onDataChange]);

	const filterTree = (nodes: ShareUniNode[]): ShareUniNode[] =>
		nodes
			.map((n) => {
				const match = n.name.toLowerCase().includes(keyword.toLowerCase()) ||
					(n.code && n.code.toLowerCase().includes(keyword.toLowerCase()));
				const children = n.children?.length ? filterTree(n.children) : [];
				if (match || children.length) {
					return { ...n, children: children.length ? children : undefined };
				}
				return null;
			})
			.filter(Boolean) as ShareUniNode[];

	const updatePermissionWithChildren = (nodeUuid: string, newPermission: string) => {
		setData(prevData => {
			const childrenUuids = new Set<string>();

			const collectChildrenUuids = (parentUuid: string) => {
				prevData.forEach(item => {
					if (item.parentUuid === parentUuid) {
						childrenUuids.add(item.uuid);
						collectChildrenUuids(item.uuid);
					}
				});
			};

			collectChildrenUuids(nodeUuid);

			return prevData.map(item => {
				if (item.uuid === nodeUuid || childrenUuids.has(item.uuid)) {
					return { ...item, permission: newPermission };
				}
				return item;
			});
		});
	};

	const updateNodeAndChildren = (node: ShareUniNode, perm: string): ShareUniNode => {
		const updatedNode = { ...node, permission: perm };
		if (updatedNode.children && updatedNode.children.length > 0) {
			updatedNode.children = updatedNode.children.map(child =>
				updateNodeAndChildren(child, perm)
			);
		}
		return updatedNode;
	};

	const deleteNodeWithChildren = (nodes: ShareUniNode[], uuid: string): ShareUniNode[] => {
		const flatData = flattenTree(nodes);
		const nodeToDelete = flatData.find(n => n.uuid === uuid);

		if (!nodeToDelete) return nodes;

		const uuidsToDelete = new Set<string>();
		const collectUuids = (node: ShareUniNode) => {
			uuidsToDelete.add(node.uuid);
			if (node.children) {
				node.children.forEach(collectUuids);
			}
		};

		const findAndCollect = (searchNodes: ShareUniNode[]) => {
			for (const node of searchNodes) {
				if (node.uuid === uuid) {
					collectUuids(node);
					return;
				}
				if (node.children) {
					findAndCollect(node.children);
				}
			}
		};

		findAndCollect(nodes);

		setData(prev => prev.filter(n => !uuidsToDelete.has(n.uuid)));

		return nodes
			.filter((n) => !uuidsToDelete.has(n.uuid))
			.map((n) => n.children
				? { ...n, children: deleteNodeWithChildren(n.children, uuid) }
				: n
			);
	};

	const flattenTree = (nodes: ShareUniNode[]): ShareUniNode[] => {
		const result: ShareUniNode[] = [];
		const traverse = (items: ShareUniNode[]) => {
			items.forEach(item => {
				result.push(item);
				if (item.children) {
					traverse(item.children);
				}
			});
		};
		traverse(nodes);
		return result;
	};

	const treeData = useMemo(
		() => filterTree(flatToTree(data)),
		[data, keyword]
	);

	const countNodes = (nodes: ShareUniNode[]) => {
		let universities = 0;
		let totalSchools = 0;

		const traverse = (nodeList: ShareUniNode[]) => {
			nodeList.forEach(node => {
				totalSchools++;
				if (node.isDaiHoc) {
					universities++;
				}
				if (node.children && Array.isArray(node.children) && node.children.length > 0) {
					traverse(node.children);
				}
			});
		};

		traverse(nodes);
		return { universities, totalSchools };
	};

	const { universities, totalSchools } = useMemo(() => countNodes(treeData), [treeData]);

	const paginatedData = useMemo(() => {
		const start = (page - 1) * pageSize;
		const end = start + pageSize;
		return treeData.slice(start, end);
	}, [treeData, page, pageSize]);

	const columns = [
		{
			title: t("Tên đơn vị đào tạo", "Tên đơn vị đào tạo"),
			dataIndex: 'name',
			key: 'name',
			render: (text: string, record: ShareUniNode) => (
				<div>
					<Typography.Text strong={record.isDaiHoc}>
						{text}
					</Typography.Text>
					{record.code && (
						<Typography.Text
							type="secondary"
							style={{ fontSize: 12, marginLeft: 8 }}
						>
							({record.code})
						</Typography.Text>
					)}
				</div>
			),
		},
		{
			title: t("Quyền", "Quyền"),
			dataIndex: 'permission',
			key: 'permission',
			width: 200,
			render: (_: any, r: ShareUniNode) => {
				if (r?.isDaiHoc) {
					return null;
				}

				return (
					<FormItemDropdown
						value={r.permission}
						onChange={(val) => {
							updatePermissionWithChildren(r.uuid, val);
						}}
						options={permissionOptions.filter((p) => p.id === PERMISSION_LEVELS.VIEW || p.id === PERMISSION_LEVELS.EDIT)}
						style={{ width: 180 }}
						description
					/>
				);
			},
		},
		{
			key: 'act',
			width: 48,
			render: (_: any, r: ShareUniNode) => (
				<Button
					danger
					type="text"
					icon={<DeleteOutlined />}
					onClick={() => {
						deleteNodeWithChildren(treeData, r.uuid);
					}}
				/>
			),
		},
	];

	const mergeData = (
		arr: {
			uuid: string;
			name: string;
			permission: string;
			parentUuid: string | null;
			isDaiHoc?: boolean;
			code?: string;
		}[]
	) => {
		setData((prev) => {
			const merged = [...prev];
			arr.forEach((it) => {
				const found = merged.find((x) => x.uuid === it.uuid);
				if (found) {
					found.permission = it.permission;
					if (it.name) found.name = it.name;
				} else {
					merged.push({
						...it,
						isDaiHoc: it.isDaiHoc || false,
						code: it.code || ''
					});
				}
			});
			return merged;
		});
	};

	const renderContentByMode = () => {
		switch (mode) {
			case ACCESS_MODES.RESTRICTED:
				return <div>{t("Chỉ những người được thêm mới có quyền truy cập", "Chỉ những người được thêm mới có quyền truy cập")}</div>;

			case ACCESS_MODES.PUBLIC:
				return (
					<div>
						<div className="mb-2">
							{t("Bất kỳ ai cũng có quyền truy cập", "Bất kỳ ai cũng có quyền truy cập")}
						</div>
						<FormItemDropdown
							description
							value={publicPermission}
							onChange={setPublicPermission}
							options={permissionOptions.filter((item) =>
								item.id === PERMISSION_LEVELS.VIEW || item.id === PERMISSION_LEVELS.EDIT
							)}
							style={{ width: '200px' }}
						/>
					</div>
				);

			case ACCESS_MODES.UNIVERSITY:
				return (
					<>
						<div style={{ marginBottom: 12 }}>
							{t("Chỉ các đơn vị đào tạo được chọn mới có quyền truy cập", "Chỉ các đơn vị đào tạo được chọn mới có quyền truy cập")}
						</div>
						<Space
							style={{
								marginBottom: 12,
								width: '100%',
								justifyContent: 'space-between',
							}}
						>
							<Button icon={<PlusOutlined />} onClick={() => setOpen(true)}>
								{t("Thêm", "Thêm")}
							</Button>
							<Search
								allowClear
								placeholder={t("Tìm kiếm theo tên hoặc mã", "Tìm kiếm theo tên hoặc mã")}
								style={{ width: 300 }}
								value={keyword}
								onChange={(e) => {
									setKeyword(e.target.value);
									setPage(1);
								}}
							/>
						</Space>

						<div className="table-wrapper">
							<Table
								columns={columns}
								dataSource={paginatedData}
								rowKey="uuid"
								expandable={{
									expandedRowKeys,
									onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as any),
									expandRowByClick: false,
									rowExpandable: (record) => !!(record.children && record.children.length > 0)
								}}
								pagination={false}
								size="small"
								locale={{
									emptyText: data.length === 0
										? 'Chưa có đơn vị đào tạo nào được chọn'
										: 'Không tìm thấy đơn vị đào tạo phù hợp'
								}}
								scroll={{ x: 1000, y: 540 }}
							/>

							{treeData.length > 0 && (
								<Pagination
									total={treeData.length}
									current={page}
									pageSize={pageSize}
									showSizeChanger
									showQuickJumper
									pageSizeOptions={['5', '10', '20', '50']}
									className="pagination-table"
									onChange={(p, s) => {
										setPage(p);
										setPageSize(s || pageSize);
									}}
									showTotal={(total) => `${t("Tổng", "Tổng")} ${universities} ${t("đại học", "đại học")} / ${totalSchools} ${t("đơn vị đào tạo", "đơn vị đào tạo")}`}
									itemRender={(_, type, original) =>
										type === "prev" ? <LeftOutlined /> :
											type === "next" ? <RightOutlined /> : original
									}
									locale={{
										items_per_page: "/ trang",
										jump_to: t("Đi đến trang", "Đi đến trang"),
										page: "",
									}}
								/>
							)}
						</div>
					</>
				);

			default:
				return null;
		}
	};

	return (
		<>
			<Radio.Group
				value={mode}
				onChange={(e) => {
					const newMode = e.target.value as ShareMode;
					setMode(newMode);
					if (newMode !== ACCESS_MODES.UNIVERSITY) {
						setData([]);
						setExpandedRowKeys([]);
						setPage(1);
					}
				}}
			>
				<Radio value={ACCESS_MODES.RESTRICTED}>
					<div className='flex gap-8 align-center'>
						<LockOutlined style={{ fontSize: 20 }} />{t("Hạn chế", "Hạn chế")}
					</div>
				</Radio>
				<Radio value={ACCESS_MODES.PUBLIC}>
					<div className='flex gap-8 align-center'>
						<GlobalOutlined style={{ fontSize: 20 }} />{t("Công khai toàn bộ", "Công khai toàn bộ")}
					</div>
				</Radio>
				<Radio value={ACCESS_MODES.UNIVERSITY}>
					<div className='flex gap-8 align-center'>
						<UsergroupAddOutlined style={{ fontSize: 20 }} /> {t("Đơn vị đào tạo", "Đơn vị đào tạo")}
					</div>
				</Radio>
			</Radio.Group>

			<Divider style={{ margin: '12px 0' }} />

			{renderContentByMode()}

			<AddUniversityPermissionModal
				open={open}
				onCancel={() => setOpen(false)}
				onOk={(arr) => {
					mergeData(arr);
					setOpen(false);
					setPage(1);
				}}
				defaultSelected={data.map(({ uuid, permission }) => ({ uuid, permission }))}
				authorizedUniversities={data.map(item => ({
					uuid: item.uuid,
					name: item.name,
					permission: item.permission
				}))}
			/>
		</>
	);
};

export default SharePermissionPublicTab;